import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import Widget from "../../Widget/Widget";
import { Button, Message, Modal } from "../../BP2Components";
import SettingsModal from "../../Modal/SettingsModal";
import CalendarSettings from "../WidgetsSettings/Calendar/CalendarSettingsModal";
import FullCalendar from "@fullcalendar/react";
import { useIntl } from "react-intl";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import allLocales from "@fullcalendar/core/locales-all";
import { getList, getListV2 } from "../../../services/api/Api";
import IntlMessages from "@util/IntlMessages";
import Form from "../../FormsV2/FormComplete";
import {
   addConnectionsToActionButtons,
   addRowValuesToConnection,
   getConnectionData,
   isActivePublicUser,
} from "../../../util/Scripts";
import "./Calendar.less";
import { handleProcessAction } from "../../../util/actions/process";

const CalendarWidget = ({ settings, role }) => {
   const [selectedEvent, setSelectedEvent] = useState({});
   const [actionButtons, setActionButtons] = useState([]);
   const [events, setEvents] = useState([]);
   const [visible, setVisible] = useState(false);
   const [newEventMode, setNewEventMode] = useState(false);
   const [currentAction, setCurrentAction] = useState();
   const [addEventFormId, setAddEventFormId] = useState();
   const [showFormId, setShowFormId] = useState();

   const { currentBoard } = useSelector(({ boards }) => boards);

   const calendarRef = useRef();
   const intl = useIntl();

   const { locale, navStyle } = useSelector(({ settings }) => settings);

   const handleEventClick = (info) => {
      setSelectedEvent({
         id: info.event.id,
         title: info.event.title,
         allDay: info.event.allDay,
         start: new Date(info.event.start),
         end: new Date(info.event.end),
      });
      setVisible(true);
   };

   const getValueFromColumnData = (columnData) => {
      if (typeof columnData === "undefined") return "";

      if (typeof columnData === "string") return columnData;

      if (Array.isArray(columnData)) return columnData[0];

      if (typeof columnData === "object" && columnData !== null) {
         const columnDataValues = Object.values(columnData);

         // If it's object, we return value of the last key
         if (columnDataValues?.length) return columnDataValues[columnDataValues.length - 1];
      }

      return "";
   };

   const handleListResponse = (res) => {
      const responseData = isActivePublicUser() ? res?.data?.getPublicList : res?.data?.default;
      let fetchedEvents = responseData?.data;

      // Here is connection between widgets handled
      let actions = [];

      if (
         currentBoard &&
         !!currentBoard?.connections?.length &&
         Array.isArray(responseData.actions) &&
         responseData.actions.length > 0
      ) {
         actions = addConnectionsToActionButtons(
            currentBoard.connections,
            responseData.actions,
            settings.id,
            settings.listId,
            intl,
         );
      }
      setActionButtons(actions.length > 0 ? actions : responseData.actions);

      const { eventId, title, allDay, startDate, endDate, startHour, endHour } =
         settings.calendarColumns;

      // Set events
      fetchedEvents = fetchedEvents.map((event) => {
         let newEvent = {
            id: getValueFromColumnData(event[eventId]),
            title: getValueFromColumnData(event[title]),
            start: `${getValueFromColumnData(event[startDate])}T${getValueFromColumnData(
               event[startHour],
            )}`,
            end: `${getValueFromColumnData(event[endDate])}T${getValueFromColumnData(
               event[endHour],
            )}`,
            allDay: false,
         };

         if (getValueFromColumnData(event[allDay]) === "TAK") {
            newEvent.start = getValueFromColumnData(event[startDate]);
            newEvent.end = getValueFromColumnData(event[endDate]);
            newEvent.allDay = true;
         }

         return newEvent;
      });

      setAddEventFormId(responseData.meta.addItemFormId);
      setShowFormId(responseData.meta.previewItemFormChildId);
      setEvents(fetchedEvents);
   };

   const changeCurrentEventToLabel = () => {
      // We need this to make filtering work, because right now we don't use labels/technames to identify
      // proper columns, but custom naming like title or start
      // TODO: change this to technames after list's columns are identified via technames

      const eventToChange = selectedEvent;
      const { eventId, title, allDay, startDate, endDate } = settings.calendarColumns;

      Object.keys(eventToChange).forEach((propertie) => {
         switch (propertie) {
            case "id":
               eventToChange[eventId] = eventToChange["id"];
               delete eventToChange["id"];
               break;
            case "title":
               eventToChange[title] = eventToChange["title"];
               delete eventToChange["title"];
               break;
            case "allDay":
               eventToChange[allDay] = eventToChange["allDay"];
               delete eventToChange["allDay"];
               break;
            case "start":
               eventToChange[startDate] = eventToChange["start"];
               delete eventToChange["start"];
               break;
            case "end":
               eventToChange[endDate] = eventToChange["end"];
               delete eventToChange["end"];
               break;
            default:
               break;
         }
      });

      return eventToChange;
   };

   // Here is all events data fetched
   useEffect(() => {
      let connectionData;
      let listFilters;

      if (window?.location) {
         const urlParams = new URLSearchParams(window.location.search);
         listFilters = JSON.parse(decodeURIComponent(urlParams.get("listFilters")));
         connectionData = getConnectionData(settings.id);
      }

      if (connectionData) {
         try {
            getListV2(settings.calendarId, 1, 999, connectionData)
               .then((res) => handleListResponse(res))
               .catch(() => {
                  Message.error(intl.formatMessage({ id: "calendar.errorInEventsFetching" }));
               });
         } catch (err) {
            getList(settings.calendarId, 1, 999)
               .then((res) => handleListResponse(res))
               .catch(() => {
                  Message.error(intl.formatMessage({ id: "calendar.errorInEventsFetching" }));
               });
         }
      } else if (listFilters?.[settings.id]) {
         try {
            getListV2(settings.calendarId, 1, 999, listFilters[settings.id]).then(
               handleListResponse,
            );
         } catch (err) {
            console.log("Error in initial render with filter in URL", err);
         }
      } else {
         getList(settings.calendarId, 1, 999).then(handleListResponse);
      }
   }, [settings.listId, newEventMode]);

   // Because hiding navbar does not change width of the document, we have to force FullCalendar to resize after hiding/showing navbar
   // setTimeout is crucial here, because it needs to wait for animation to end
   useEffect(() => {
      let calendarApi = calendarRef.current.getApi();

      setTimeout(() => {
         calendarApi.updateSize();
      }, 250);
   }, [navStyle]);

   const handleCloseModal = () => {
      setVisible(false);
      setNewEventMode(false);
      setCurrentAction();
      setSelectedEvent({});
   };

   const handleAddEvent = () => {
      // When form to add new events is not provided in list configuration, API sends us 0 as form ID, so we need to check this too.
      if (addEventFormId && addEventFormId !== "0") {
         setNewEventMode(true);
         setVisible(true);
      } else {
         Message.error(intl.formatMessage({ id: "calendar.noAddFormId" }));
      }
   };

   return (
      <Widget
         styleName="gx-m-0"
         bgColor={settings.backgroundColourHex}
         title={settings.noteTitle}
         role={role}
         extra={
            (role === "OWNER" || role === "MODERATOR") && (
               <SettingsModal
                  widgetName={settings.name}
                  widgetId={settings.id}
                  icon="setting"
                  deleteIcon="true"
                  children={<CalendarSettings value={settings} />}
               />
            )
         }
      >
         <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin]}
            initialView="dayGridMonth"
            events={events}
            eventClick={handleEventClick}
            locales={allLocales}
            locale={locale}
            ref={calendarRef}
            aspectRatio={settings?.aspectRatio ? Number(settings.aspectRatio) : 3}
            customButtons={{
               addNewEvent: {
                  text: intl.formatMessage({ id: "calendar.addEvent" }),
                  click: handleAddEvent,
               },
            }}
            headerToolbar={{
               right: `${
                  // The space after addNewEvent is VERY important. Do NOT remove it or it won't work properly
                  // Is you remove the space and add one after this conditional, FullCalendar will display empty button.
                  settings?.showNewEventButton ? "addNewEvent " : ""
               }dayGridMonth,timeGridWeek,timeGridDay today prev,next`,
            }}
         />
         <Modal isOpen={visible} onCancel={handleCloseModal} wrapClassName="calendar-modal">
            {!newEventMode && (
               <div className="calendar-action-buttons">
                  {actionButtons.map((actionButton) => (
                     <Button
                        key={actionButton.id}
                        buttonType="primary"
                        onClick={() => {
                           // Here we handle connection redirect url
                           if (actionButton.connectionsLink) {
                              window.location.replace(
                                 addRowValuesToConnection(
                                    actionButton.connectionsLink,
                                    changeCurrentEventToLabel(),
                                    settings.id,
                                 ),
                              );
                           } else if (actionButton?.type === "FORM") {
                              setCurrentAction({
                                 formId: actionButton.formID,
                                 actionId: actionButton.id,
                                 dataId: selectedEvent.id,
                              });
                           } else {
                              handleProcessAction(actionButton, selectedEvent.id);
                           }
                        }}
                     >
                        {actionButton.label}
                     </Button>
                  ))}
               </div>
            )}
            {newEventMode ? (
               <Form formId={addEventFormId} />
            ) : currentAction ? (
               <Form actionId={currentAction.actionId} recordId={currentAction.dataId} />
            ) : showFormId && showFormId != 0 && selectedEvent.id ? (
               <Form
                  formId={showFormId}
                  dataId={selectedEvent.id}
                  isReadOnly={true}
                  isSubmitVisible={false}
               />
            ) : (
               <IntlMessages id="calendar.noShowFormId" />
            )}
         </Modal>
      </Widget>
   );
};

export default CalendarWidget;
